import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

const StudentMarksEntry = ({ 
  assignmentId, 
  student, 
  questionStructure, 
  onSave, 
  totalMaxMarks, 
  isSimple, 
  componentName, 
  readOnly = false,
  isEndExam = false  // NEW PROP
}) => {
  const [marksData, setMarksData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setTotalObtained] = useState(0);
  const [totalMax, setTotalMax] = useState(0);
  const [, setPercentage] = useState(0);
  const [simpleMark, setSimpleMark] = useState(0);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [displayComponentName, setDisplayComponentName] = useState(componentName || (isEndExam ? 'End Exam' : 'Assessment'));

  // Determine API endpoint based on type
  const getApiEndpoint = useCallback(() => {
    if (isEndExam) {
      return `/api/faculty/end-exam-marks/${assignmentId}/students/${student?.studentId}`;
    }
    return `/api/faculty/marks-entry/${assignmentId}/students/${student?.studentId}`;
  }, [assignmentId, student?.studentId, isEndExam]);

  // Auto-dismiss toast notification
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const showNotification = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const loadStudentMarks = useCallback(async () => {
    if (!student || !student.studentId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = getApiEndpoint();
      const res = await api.get(endpoint);
      
      if (res.data.success) {
        const data = res.data.data;
        
        if (isSimple || (data.marksData && Object.keys(data.marksData).length === 1 && data.marksData.single_question)) {
          const singleMark = data.marksData?.single_question?.marksObtained || data.totalMarksObtained || 0;
          setSimpleMark(singleMark);
          setTotalObtained(singleMark);
          setTotalMax(data.totalMaxMarks || totalMaxMarks || 100);
          setPercentage(data.percentage || 0);
        } else {
          const existingMarksData = data.marksData || {};
          setMarksData(existingMarksData);
          setTotalObtained(data.totalMarksObtained || 0);
          setTotalMax(data.totalMaxMarks || 0);
          setPercentage(data.percentage || 0);
        }
        
        console.log(`Loaded ${isEndExam ? 'End Exam' : 'CIE'} marks for student:`, student.studentName);
      }
    } catch (error) {
      console.error('Error loading student marks:', error);
      
      if (isSimple || (questionStructure && questionStructure.length === 0)) {
        setSimpleMark(0);
        setTotalMax(totalMaxMarks || 100);
      } else if (questionStructure && questionStructure.length > 0) {
        const emptyMarks = {};
        questionStructure.forEach(q => {
          emptyMarks[q.questionId] = {
            marksObtained: 0,
            maxMarks: q.maxMarks,
            questionText: q.questionText,
            questionNumber: q.questionNumber,
            coMappings: q.coMappings,
            poMappings: q.poMappings
          };
        });
        setMarksData(emptyMarks);
        
        const totalMaxVal = questionStructure.reduce((sum, q) => sum + q.maxMarks, 0);
        setTotalMax(totalMaxVal);
      }
    } finally {
      setLoading(false);
    }
  }, [student, isSimple, questionStructure, totalMaxMarks, isEndExam, getApiEndpoint]);

  useEffect(() => {
    if (assignmentId && student && student.studentId) {
      loadStudentMarks();
    }
  }, [assignmentId, student, loadStudentMarks]);

  useEffect(() => {
    if (componentName) {
      setDisplayComponentName(componentName);
    } else if (isEndExam) {
      setDisplayComponentName('End Exam');
    }
  }, [componentName, isEndExam]);

  const handleMarksChange = (questionId, value) => {
    if (readOnly) return;
    
    const numValue = value === '' ? '' : parseFloat(value);
    const question = questionStructure?.find(q => q.questionId === questionId);
    
    if (question && numValue !== '' && numValue > question.maxMarks) {
      showNotification(`Marks cannot exceed max value of ${question.maxMarks}`, 'error');
      return;
    }
    
    setMarksData(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        marksObtained: numValue
      }
    }));
  };

  const handleSimpleMarkChange = (value) => {
    if (readOnly) return;
    
    const numValue = value === '' ? '' : parseFloat(value);
    const maxVal = totalMaxMarks || totalMax || 100;
    
    if (numValue !== '' && numValue > maxVal) {
      showNotification(`Marks cannot exceed max value of ${maxVal}`, 'error');
      return;
    }
    
    setSimpleMark(numValue);
  };

  const calculateCurrentTotal = () => {
    let obtained = 0;
    let max = 0;
    
    if (isSimple || (questionStructure && questionStructure.length === 0)) {
      const markVal = simpleMark === '' ? 0 : (simpleMark || 0);
      obtained = markVal;
      max = totalMaxMarks || totalMax || 100;
      return { obtained, max, percent: max > 0 ? (obtained / max) * 100 : 0 };
    }
    
    if (marksData && Object.keys(marksData).length > 0) {
      Object.values(marksData).forEach(marks => {
        if (marks) {
          const val = marks.marksObtained === '' ? 0 : (marks.marksObtained || 0);
          obtained += val;
          max += marks.maxMarks || 0;
        }
      });
    } else if (questionStructure && questionStructure.length > 0) {
      max = questionStructure.reduce((sum, q) => sum + q.maxMarks, 0);
    }
    
    return { obtained, max, percent: max > 0 ? (obtained / max) * 100 : 0 };
  };

  const handleSave = async () => {
    if (readOnly) return;
    
    if (!student || !student.studentId) {
      showNotification('Invalid student data structure', 'error');
      return;
    }
    
    setSaving(true);
    
    try {
      let marksToSave = {};
      let currentMarkPayload = {};
      
      if (isSimple || (questionStructure && questionStructure.length === 0)) {
        const markValue = simpleMark === '' ? 0 : (simpleMark || 0);
        currentMarkPayload = {
          totalMarksObtained: markValue,
          totalMaxMarks: totalMaxMarks || totalMax || 100
        };
        marksToSave = {
          single_question: {
            marksObtained: markValue,
            maxMarks: totalMaxMarks || totalMax || 100
          }
        };
      } else {
        if (marksData) {
          Object.keys(marksData).forEach(questionId => {
            const val = marksData[questionId]?.marksObtained;
            marksToSave[questionId] = {
              marksObtained: val === '' || val === undefined ? 0 : val
            };
          });
        }
      }
      
      const endpoint = getApiEndpoint();
      const response = await api.post(endpoint, { marksData: marksToSave });
      const savedData = response.data?.data || {};
      
      showNotification(`${isEndExam ? 'End Exam' : 'CIE'} marks saved successfully!`, 'success');
      if (savedData.totalMarksObtained !== undefined) {
        setTotalObtained(savedData.totalMarksObtained);
      } else if (currentMarkPayload.totalMarksObtained !== undefined) {
        setTotalObtained(currentMarkPayload.totalMarksObtained);
      }

      if (savedData.totalMaxMarks !== undefined) {
        setTotalMax(savedData.totalMaxMarks);
      } else if (currentMarkPayload.totalMaxMarks !== undefined) {
        setTotalMax(currentMarkPayload.totalMaxMarks);
      }

      if (savedData.percentage !== undefined) {
        setPercentage(savedData.percentage);
      }

      if (onSave) {
        onSave({
          studentId: student.studentId,
          studentUSN: student.studentUSN,
          studentName: student.studentName,
          totalMarksObtained: savedData.totalMarksObtained ?? currentMarkPayload.totalMarksObtained,
          totalMaxMarks: savedData.totalMaxMarks ?? currentMarkPayload.totalMaxMarks,
          percentage: savedData.percentage,
          grade: savedData.grade,
          status: savedData.status
        });
      }
      
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to save marks', 'error');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateCurrentTotal();

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-4"></div>
        <div className="text-gray-500 font-medium text-sm">
          Loading marks for {student?.studentName || 'student'}...
        </div>
      </div>
    );
  }

  // ========== SIMPLE ASSIGNMENT MODE ==========
  const isSimpleMode = isSimple || (questionStructure && questionStructure.length === 0);
  
  if (isSimpleMode) {
    const maxMarksValue = totalMaxMarks || totalMax || 100;
    const currentMark = simpleMark === '' ? 0 : (simpleMark || 0);
    const currentPercent = maxMarksValue > 0 ? (currentMark / maxMarksValue) * 100 : 0;
    
    return (
      <div className="w-full max-w-2xl mx-auto bg-slate-50 md:bg-white rounded-2xl md:shadow-md md:border border-gray-100 overflow-hidden font-sans antialiased">
        
        {/* Toast Notification */}
        {toast.show && (
          <div className={`fixed bottom-5 right-5 z-50 flex items-center px-5 py-3.5 rounded-xl shadow-xl text-white font-medium text-sm transition-all duration-300 ${
            toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
          }`}>
            <span className="mr-2.5">{toast.type === 'error' ? '⚠️' : '✨'}</span>
            {toast.message}
          </div>
        )}

        {/* Student Info Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 md:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg border border-blue-100 shadow-sm shrink-0">
                {(student?.studentName || 'U').charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 leading-tight">{student?.studentName || 'Unknown Student'}</h2>
                <p className="text-xs font-mono tracking-wider text-gray-500 mt-0.5">USN: {student?.studentUSN || 'N/A'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 bg-gray-50 md:bg-slate-50 border border-gray-100 rounded-xl p-3 sm:py-2 sm:px-4">
              <div className="text-left sm:text-right">
                <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Score</span>
                <div className="text-lg font-extrabold text-gray-900">
                  {currentMark} <span className="text-gray-400 font-normal text-sm">/ {maxMarksValue}</span>
                </div>
              </div>
              <div className="h-px w-6 bg-gray-200 rotate-90 hidden sm:block"></div>
              <div className="text-right">
                <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Percentage</span>
                <div className={`text-lg font-black ${currentPercent >= 40 ? 'text-emerald-600' : 'text-amber-500'}`}>
                  {currentPercent.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Simple Marks Input Area */}
        <div className="p-6">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-gray-200">
              <h3 className="font-bold text-sm text-gray-800 tracking-wide uppercase">
                {displayComponentName} Marks Entry {readOnly && '(Read Only)'}
              </h3>
            </div>
            
            <div className="p-6">
              <div className="flex flex-col items-center justify-center gap-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-full border border-blue-100 mb-3">
                    <span className="text-2xl font-bold text-blue-600">📝</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">
                    {readOnly ? 'View marks for' : 'Enter marks for'} {student?.studentName}
                  </p>
                  <p className="text-xs text-gray-400">
                    Maximum marks: {maxMarksValue}
                  </p>
                </div>
                
                <div className="w-full max-w-xs">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Marks Obtained
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max={maxMarksValue}
                    placeholder="0"
                    value={simpleMark}
                    onChange={(e) => handleSimpleMarkChange(e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    readOnly={readOnly}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-xl text-center font-bold text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg bg-white ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
                  />
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Score: {currentMark} / {maxMarksValue} ({currentPercent.toFixed(1)}%)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Panel */}
        <div className="p-4 md:p-6 bg-slate-100/80 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="w-full sm:w-auto flex justify-around sm:justify-start items-center gap-6 text-center sm:text-left">
            <div>
              <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Score</span>
              <span className="text-base font-extrabold text-blue-700">
                {currentMark} <span className="text-gray-400 font-normal text-xs">/ {maxMarksValue}</span>
              </span>
            </div>
            <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
            <div>
              <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Percentage</span>
              <span className="text-base font-extrabold text-emerald-600">
                {currentPercent.toFixed(1)}%
              </span>
            </div>
          </div>
          
          {!readOnly && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white rounded-xl font-bold tracking-wide text-sm shadow-md hover:bg-blue-700 active:transform active:scale-98 disabled:opacity-40 transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Marks</span>
              )}
            </button>
          )}
        </div>

        <style>{`
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type="number"] {
            -moz-appearance: textfield;
            appearance: textfield;
          }
        `}</style>
      </div>
    );
  }

  // ========== PAPER TEMPLATE MODE ==========
  const groupedQuestions = {};
  if (questionStructure && questionStructure.length > 0) {
    questionStructure.forEach(question => {
      const section = question.sectionName || 'General Section';
      if (!groupedQuestions[section]) {
        groupedQuestions[section] = [];
      }
      groupedQuestions[section].push(question);
    });
  }

  if (Object.keys(groupedQuestions).length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="text-gray-400 mb-2">📊</div>
        <div className="text-gray-500 font-medium">No question structure defined for this assessment.</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto bg-slate-50 md:bg-white rounded-2xl md:shadow-md md:border border-gray-100 overflow-hidden font-sans antialiased">
      
      {toast.show && (
        <div className={`fixed bottom-5 right-5 z-50 flex items-center px-5 py-3.5 rounded-xl shadow-xl text-white font-medium text-sm transition-all duration-300 transform translate-y-0 ${
          toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
        }`}>
          <span className="mr-2.5">{toast.type === 'error' ? '⚠️' : '✨'}</span>
          {toast.message}
        </div>
      )}

      {/* Sticky Quick-Info Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 md:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg border border-blue-100 shadow-sm shrink-0">
              {(student?.studentName || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-tight">{student?.studentName || 'Unknown Student'}</h2>
              <p className="text-xs font-mono tracking-wider text-gray-500 mt-0.5">USN: {student?.studentUSN || 'N/A'}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between sm:justify-end gap-4 bg-gray-50 md:bg-slate-50 border border-gray-100 rounded-xl p-3 sm:py-2 sm:px-4">
            <div className="text-left sm:text-right">
              <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Aggregate Score</span>
              <div className="text-lg font-extrabold text-gray-900">
                {totals.obtained.toFixed(1)} <span className="text-gray-400 font-normal text-sm">/ {totals.max}</span>
              </div>
            </div>
            <div className="h-px w-6 bg-gray-200 rotate-90 hidden sm:block"></div>
            <div className="text-right">
              <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Percentage</span>
              <div className={`text-lg font-black ${totals.percent >= 40 ? 'text-emerald-600' : 'text-amber-500'}`}>
                {totals.percent.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-3 md:p-6 space-y-6">
        {Object.keys(groupedQuestions).map((sectionName) => {
          const sectionQuestions = groupedQuestions[sectionName];
          const sectionTotalMax = sectionQuestions.reduce((sum, q) => sum + q.maxMarks, 0);
          const sectionTotalObtained = sectionQuestions.reduce((sum, q) => {
            const val = marksData[q.questionId]?.marksObtained;
            return sum + (val === '' ? 0 : (val || 0));
          }, 0);
          
          return (
            <div key={sectionName} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              
              <div className="bg-slate-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-bold text-sm text-gray-800 tracking-wide uppercase">
                  {sectionName} {readOnly && '(Read Only)'}
                </h3>
                <span className="bg-white border border-gray-300 text-xs font-semibold px-2.5 py-1 rounded-md text-gray-600 shadow-2xs">
                  Sec Total: <b className="text-blue-600 font-bold">{sectionTotalObtained.toFixed(1)}</b> / {sectionTotalMax}
                </span>
              </div>
              
              {/* DESKTOP TABLE VIEW */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-gray-200">
                      <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-20">Q.No</th>
                      <th className="px-4 py-2.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Evaluation Description</th>
                      <th className="px-4 py-2.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-28">Max Scale</th>
                      <th className="px-4 py-2.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-36">Obtained Marks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sectionQuestions.map((question) => {
                      const savedMark = marksData[question.questionId];
                      const obtainedValue = savedMark?.marksObtained !== undefined ? savedMark.marksObtained : '';
                      
                      return (
                        <tr key={question.questionId} className="hover:bg-blue-50/30 transition-colors group">
                          <td className="px-4 py-3 text-sm font-mono font-bold text-blue-600 bg-slate-50/30 group-hover:bg-blue-50/50 text-center border-r border-gray-100">
                            {question.questionNumber}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                            {question.questionText || <span className="text-gray-400 italic">No custom text provided</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-sm font-semibold text-gray-500 bg-slate-50/20">
                            {question.maxMarks}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="inline-flex items-center justify-center relative">
                              <input
                                type="number"
                                step="any"
                                min="0"
                                max={question.maxMarks}
                                placeholder="0"
                                value={obtainedValue}
                                onChange={(e) => handleMarksChange(question.questionId, e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                readOnly={readOnly}
                                className={`w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-center font-bold text-gray-800 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all bg-white ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE STACKED VIEW */}
              <div className="block md:hidden divide-y divide-gray-100">
                {sectionQuestions.map((question) => {
                  const savedMark = marksData[question.questionId];
                  const obtainedValue = savedMark?.marksObtained !== undefined ? savedMark.marksObtained : '';
                  
                  return (
                    <div key={question.questionId} className="p-4 active:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <span className="inline-flex items-center justify-center bg-blue-50 border border-blue-200 text-blue-700 rounded-md font-mono font-black px-2.5 py-0.5 text-xs">
                          Q-{question.questionNumber}
                        </span>
                        <div className="text-right text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          Max: <b className="text-gray-700 font-bold">{question.maxMarks}</b>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-600 font-medium leading-relaxed mb-3">
                        {question.questionText || <span className="text-gray-400 italic">Standard assessment</span>}
                      </p>
                      
                      <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-lg border border-dashed border-gray-200">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-400 w-full">
                          Marks Awarded:
                        </label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          max={question.maxMarks}
                          placeholder="0"
                          value={obtainedValue}
                          onChange={(e) => handleMarksChange(question.questionId, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          readOnly={readOnly}
                          className={`w-28 px-3 py-2 border border-gray-300 rounded-lg text-center font-black text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white ${readOnly ? 'bg-slate-50 text-slate-500' : ''}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          );
        })}
      </div>

      {/* Bottom Action Panel */}
      <div className="p-4 md:p-6 bg-slate-100/80 border-t border-gray-200 backdrop-blur-xs flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="w-full sm:w-auto flex justify-around sm:justify-start items-center gap-6 text-center sm:text-left">
          <div>
            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Final Aggregation</span>
            <span className="text-base font-extrabold text-blue-700">
              {totals.obtained.toFixed(1)} <span className="text-gray-400 font-normal text-xs">/ {totals.max}</span>
            </span>
          </div>
          <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
          <div>
            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">Percentage</span>
            <span className="text-base font-extrabold text-emerald-600">
              {totals.percent.toFixed(1)}%
            </span>
          </div>
        </div>
        
        {!readOnly && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white rounded-xl font-bold tracking-wide text-sm shadow-md hover:bg-blue-700 active:transform active:scale-98 disabled:opacity-40 transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Processing...</span>
              </>
            ) : (
              <span>Save Marks</span>
            )}
          </button>
        )}
      </div>

      <style>{`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>
    </div>
  );
};

export default StudentMarksEntry;